import { HIGHLIGHT_COLORS } from "@wiki/core";
import { useRef, useState, type ReactNode } from "react";
import type { EditorState } from "prosemirror-state";
import type { Command } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import { blockTypeActive, toggleBlockquote, toggleHeading } from "../commands/blocks";
import { wrapInToggle } from "../commands/containers";
import {
  BULLET_LIST,
  TODO_LIST,
  indentListItem,
  listActive,
  orderedList,
  outdentListItem,
  toggleList,
} from "../commands/lists";
import {
  markActive,
  setLink,
  toggleCodeMark,
  toggleEm,
  toggleHighlight,
  toggleStrikethrough,
  toggleStrong,
  togglePlaceholder,
} from "../commands/marks";
import {
  cellBackground,
  isInTable,
  mergeCells,
  setCellBackground,
  splitCell,
} from "../commands/tables";
import type { EditorContext } from "../context";
import { anchorSelection } from "../plugins/comments";
import { openLinkToolbar } from "../plugins/link-toolbar";
import { findParentOfAnyType, findParentOfType, marks, nodes } from "../schema";
import { sliceToMarkdown } from "../paste";
import {
  MenuPopover,
  Separator,
  ToolbarButton,
  placementAboveSelection,
  useRovingFocus,
} from "./MenuPopover";

export interface SelectionToolbarProps {
  view: EditorView;
  state: EditorState;
  context: EditorContext;
  /** A template document also offers the placeholder mark (§3, item 10). */
  template?: boolean;
}

function copySelection(view: EditorView): void {
  const slice = view.state.selection.content();
  void navigator.clipboard.writeText(sliceToMarkdown(slice));
}

/**
 * The selection toolbar of `editor.md` §3, in the order the spec lists: block
 * conversions, table cell operations, lists and indentation, the placeholder,
 * the marks, highlight swatches, code, link, comment, copy. Items that cannot
 * apply to the selection are hidden, not disabled, and separators collapse
 * with them.
 *
 * Read-only reduces it to Copy and Comment (§15).
 */
export function SelectionToolbar({
  view,
  state,
  context,
  template = false,
}: SelectionToolbarProps): ReactNode {
  const containerRef = useRef<HTMLDivElement>(null);
  const [swatches, setSwatches] = useState<"none" | "highlight" | "cell">("none");
  useRovingFocus(containerRef);

  if (state.selection.empty) return null;
  if (!state.selection.content().content.size) return null;

  const run = (command: Command): void => {
    command(view.state, view.dispatch, view);
    view.focus();
  };

  const inTable = isInTable(state);
  // A header cell can carry a background just as a body cell can.
  const cell = findParentOfAnyType(state.selection.$from, [nodes.tableCell, nodes.tableHeader]);
  const placement = placementAboveSelection(view);

  if (context.readOnly) {
    return (
      <MenuPopover
        placement={placement}
        role="toolbar"
        label="Selection"
        testId="selection-toolbar"
      >
        <div ref={containerRef} className="wiki-menu__row">
          <ToolbarButton
            label="Copy"
            onSelect={() => {
              copySelection(view);
            }}
          />
          <ToolbarButton
            label="Comment"
            onSelect={() => {
              context.onCreateComment(anchorSelection(state));
            }}
          />
        </div>
      </MenuPopover>
    );
  }

  return (
    <MenuPopover placement={placement} role="toolbar" label="Selection" testId="selection-toolbar">
      <div ref={containerRef} className="wiki-menu__row">
        <ToolbarButton
          label="Heading"
          active={blockTypeActive(state, nodes.heading, { level: 1 })}
          onSelect={() => {
            run(toggleHeading(1));
          }}
        />
        <ToolbarButton
          label="Subheading"
          active={blockTypeActive(state, nodes.heading, { level: 2 })}
          onSelect={() => {
            run(toggleHeading(2));
          }}
        />
        <ToolbarButton
          label="Small heading"
          active={blockTypeActive(state, nodes.heading, { level: 3 })}
          onSelect={() => {
            run(toggleHeading(3));
          }}
        />
        <ToolbarButton
          label="Quote"
          active={findParentOfType(state.selection.$from, nodes.blockquote) !== null}
          onSelect={() => {
            run(toggleBlockquote);
          }}
        />
        <Separator />
        {inTable && (
          <>
            <ToolbarButton
              label="Merge cells"
              onSelect={() => {
                run(mergeCells);
              }}
            />
            <ToolbarButton
              label="Split cell"
              onSelect={() => {
                run(splitCell);
              }}
            />
          </>
        )}
        <ToolbarButton
          label="Toggle block"
          onSelect={() => {
            run(wrapInToggle(null));
          }}
        />
        <Separator />
        <ToolbarButton
          label="Todo list"
          active={listActive(state, TODO_LIST)}
          onSelect={() => {
            run(toggleList(TODO_LIST));
          }}
        />
        <ToolbarButton
          label="Bulleted list"
          active={listActive(state, BULLET_LIST)}
          onSelect={() => {
            run(toggleList(BULLET_LIST));
          }}
        />
        <ToolbarButton
          label="Ordered list"
          active={listActive(state, orderedList("number"))}
          onSelect={() => {
            run(toggleList(orderedList("number")));
          }}
        />
        <ToolbarButton
          label="Outdent"
          onSelect={() => {
            run(outdentListItem);
          }}
        />
        <ToolbarButton
          label="Indent"
          onSelect={() => {
            run(indentListItem);
          }}
        />
        <Separator />
        {template && (
          <>
            <ToolbarButton
              label="Placeholder"
              active={markActive(state, marks.placeholder)}
              onSelect={() => {
                run(togglePlaceholder);
              }}
            />
            <Separator />
          </>
        )}
        <ToolbarButton
          label="Bold"
          active={markActive(state, marks.strong)}
          onSelect={() => {
            run(toggleStrong);
          }}
        />
        <ToolbarButton
          label="Italic"
          active={markActive(state, marks.em)}
          onSelect={() => {
            run(toggleEm);
          }}
        />
        <ToolbarButton
          label="Strikethrough"
          active={markActive(state, marks.strikethrough)}
          onSelect={() => {
            run(toggleStrikethrough);
          }}
        />
        {cell && (
          <ToolbarButton
            label="Background color"
            active={swatches === "cell"}
            onSelect={() => {
              setSwatches(swatches === "cell" ? "none" : "cell");
            }}
          />
        )}
        <ToolbarButton
          label="Highlight"
          active={markActive(state, marks.highlight)}
          onSelect={() => {
            setSwatches(swatches === "highlight" ? "none" : "highlight");
          }}
        />
        {swatches !== "none" && (
          <span className="wiki-menu__swatches" role="group" aria-label="Colours">
            <ToolbarButton
              label="None"
              onSelect={() => {
                run(swatches === "cell" ? setCellBackground(null) : toggleHighlight(null));
                setSwatches("none");
              }}
            />
            {HIGHLIGHT_COLORS.map((color) => (
              <button
                key={color}
                type="button"
                className="wiki-menu__swatch"
                style={{ backgroundColor: color }}
                aria-label={color}
                data-color={color}
                aria-pressed={
                  swatches === "cell" && cell ? cellBackground(cell.node) === color : undefined
                }
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
                onClick={() => {
                  run(swatches === "cell" ? setCellBackground(color) : toggleHighlight(color));
                  setSwatches("none");
                }}
              />
            ))}
          </span>
        )}
        <ToolbarButton
          label="Code"
          active={markActive(state, marks.code)}
          onSelect={() => {
            run(toggleCodeMark);
          }}
        />
        <Separator />
        <ToolbarButton
          label="Create link"
          active={markActive(state, marks.link)}
          onSelect={() => {
            run(setLink(""));
            openLinkToolbar(view);
          }}
        />
        <ToolbarButton
          label="Comment"
          onSelect={() => {
            context.onCreateComment(anchorSelection(state));
          }}
        />
        <Separator />
        <ToolbarButton
          label="Copy"
          onSelect={() => {
            copySelection(view);
          }}
        />
      </div>
    </MenuPopover>
  );
}
